import type { Job, JobStatus, JobType } from '@xphoto/shared';

/**
 * Logique PURE d'agrégation de l'activité des jobs (cloche de notifications).
 *
 * Fusionne un SEED (`GET /api/jobs`) avec le flux d'événements SSE dans une map
 * par `id` (l'événement le plus récent gagne), puis dérive deux listes prêtes à
 * afficher : les jobs ACTIFS (queued/running, toujours visibles) et les jobs
 * TERMINÉS RÉCENTS (terminaux < 24 h, non « dismissés »).
 *
 * Aucun effet de bord dans les fonctions de calcul (elles reçoivent `now` en
 * paramètre → purge 24 h testable de façon déterministe). La persistance des
 * `id` masqués (« dismiss ») est isolée dans des helpers `localStorage`
 * tolérants (jamais d'exception : mode privé, quota, environnement de test sans
 * `localStorage`).
 *
 * ⚠ Durabilité du dismiss (correctif v0.25.0) : chaque `id` masqué est stocké
 * AVEC son instant de masquage (`Map<id, dismissedAt>`). La croissance de cet
 * ensemble est bornée par le TEMPS (purge à 24 h, même fenêtre que la rétention
 * d'un terminé), JAMAIS par « le job est-il encore renvoyé par le SEED ? ».
 * L'ancienne stratégie (n'garder que les `id` présents dans `jobs`) effaçait
 * tous les dismiss au rechargement : au boot `jobs` est VIDE, l'élagage vidait
 * `localStorage`, puis le SEED re-renvoyait les terminés < 24 h qui, n'étant
 * plus masqués, RÉAPPARAISSAIENT. Un dismiss encore < 24 h survit désormais au F5.
 */

/** Fenêtre de rétention d'un job terminé avant auto-purge de la cloche (24 h). */
export const RECENT_WINDOW_MS = 24 * 60 * 60 * 1000;

/** Clé `localStorage` de l'ensemble des jobs terminés masqués par l'utilisateur. */
export const DISMISSED_STORAGE_KEY = 'xphoto:dismissedJobs';

/** Statuts terminaux (le job ne progressera plus). */
const TERMINAL_STATUSES: ReadonlySet<JobStatus> = new Set<JobStatus>([
  'done',
  'failed',
  'canceled',
]);

/** Un job terminal ne progressera plus (done/failed/canceled). */
export function isTerminal(job: Job): boolean {
  return TERMINAL_STATUSES.has(job.status);
}

/** Un job actif est en attente ou en cours (queued/running). */
export function isActive(job: Job): boolean {
  return !isTerminal(job);
}

/**
 * Fusionne le SEED et les événements SSE reçus par `id`. Le job le PLUS RÉCENT
 * l'emporte : on compare d'abord un timestamp de mise à jour (`finishedAt` >
 * `startedAt` > `createdAt`), puis, à égalité, on préfère un état terminal (un
 * `finished` arrivé après un `progress` du même job ne doit pas être écrasé).
 */
export function mergeJobs(previous: ReadonlyMap<string, Job>, incoming: Job): Map<string, Job> {
  const next = new Map(previous);
  const existing = next.get(incoming.id);
  if (existing === undefined || shouldReplace(existing, incoming)) {
    next.set(incoming.id, incoming);
  }
  return next;
}

/**
 * Fusionne une LISTE de jobs (SEED initial OU polling de repli) dans la map
 * existante, par `id`, avec la même règle que `mergeJobs` — en UNE seule copie de map.
 */
export function mergeList(
  previous: ReadonlyMap<string, Job>,
  incoming: readonly Job[],
): Map<string, Job> {
  const next = new Map(previous);
  for (const job of incoming) {
    const existing = next.get(job.id);
    if (existing === undefined || shouldReplace(existing, job)) next.set(job.id, job);
  }
  return next;
}

/** Construit une map par `id` à partir d'une liste (SEED initial). */
export function jobsById(jobs: readonly Job[]): Map<string, Job> {
  return mergeList(new Map(), jobs);
}

/** Instant de dernière mise à jour connu d'un job (ms epoch). */
function updatedAt(job: Job): number {
  const stamp = job.finishedAt ?? job.startedAt ?? job.createdAt;
  const ms = Date.parse(stamp);
  return Number.isNaN(ms) ? 0 : ms;
}

/** Le job `incoming` doit-il remplacer `existing` dans la map fusionnée ? */
function shouldReplace(existing: Job, incoming: Job): boolean {
  // Ne JAMAIS régresser un job terminé vers un état actif : un `progress` retardataire
  // peut arriver après le `finished` (flux réordonné, SSE et polling concurrents).
  if (isTerminal(existing) && !isTerminal(incoming)) return false;
  // Un état terminal l'emporte toujours sur un état actif.
  if (!isTerminal(existing) && isTerminal(incoming)) return true;
  // Deux états ACTIFS du même job : la progression est MONOTONE → le `done` le plus
  // ÉLEVÉ gagne. ⚠ Les `progress` d'un job en cours partagent le MÊME `startedAt`
  // (il n'a démarré qu'une fois) : se fier au timestamp figeait la barre à 0 (chaque
  // événement était jugé « pas plus récent »). C'était LE bug de la progression figée.
  if (!isTerminal(existing) && !isTerminal(incoming)) return incoming.done >= existing.done;
  // Deux états terminaux : le plus récemment terminé gagne.
  return updatedAt(incoming) >= updatedAt(existing);
}

/** Tri par récence décroissante (le plus récemment mis à jour en premier). */
function byRecencyDesc(a: Job, b: Job): number {
  return updatedAt(b) - updatedAt(a);
}

/** Résultat agrégé prêt pour l'affichage de la cloche. */
export interface JobActivity {
  /** Jobs en attente / en cours, triés du plus récent au plus ancien. */
  active: Job[];
  /** Jobs terminés depuis moins de 24 h, non masqués, triés par récence. */
  recent: Job[];
  /** Nombre de jobs actifs (pour le badge de la cloche). */
  activeCount: number;
}

/**
 * Dérive l'activité affichable à partir de la map fusionnée, de l'ensemble des
 * `id` masqués et de l'instant courant `now` (injecté → purge 24 h déterministe
 * en test). Les jobs actifs restent TOUJOURS visibles ; les jobs terminaux sont
 * masqués s'ils sont « dismissés » OU terminés depuis plus de 24 h.
 */
export function computeActivity(
  jobs: ReadonlyMap<string, Job>,
  dismissed: ReadonlySet<string>,
  now: number,
): JobActivity {
  const active: Job[] = [];
  const recent: Job[] = [];
  for (const job of jobs.values()) {
    if (isActive(job)) {
      active.push(job);
      continue;
    }
    if (dismissed.has(job.id)) continue;
    const finished = job.finishedAt !== null ? Date.parse(job.finishedAt) : NaN;
    // Terminé sans horodatage exploitable : on le garde visible (prudence).
    if (!Number.isNaN(finished) && now - finished > RECENT_WINDOW_MS) continue;
    recent.push(job);
  }
  active.sort(byRecencyDesc);
  recent.sort(byRecencyDesc);
  return { active, recent, activeCount: active.length };
}

/**
 * Y a-t-il un job ACTIF (en attente/en cours) du type donné dans l'activité
 * agrégée ? Sélecteur PUR — utilisé pour verrouiller le lancement d'un scan
 * mono-instance (l'entrée « Lancer un scan » du menu Outils est grisée tant qu'un
 * job `scan` est actif). On lit `activity.active` (déjà filtré queued/running).
 */
export function hasActiveJobOfType(activity: JobActivity, type: JobType): boolean {
  return activity.active.some((job) => job.type === type);
}

/** Champ numérique fini de `result` (jsonb libre), ou `null` si absent/non numérique. */
function numberField(result: Record<string, unknown>, key: string): number | null {
  const value = result[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * Un job « Terminé » (done) a-t-il en réalité TOUT échoué ? À ce jour SEUL le tagging
 * IA (`ai_tag`) présente ce piège : il compte ses échecs PAR PHOTO (fournisseur
 * injoignable, réponse illisible) sans jamais échouer globalement → il finit `done` en
 * n'ayant RIEN produit, ce qui ressemble à tort à un succès vert dans la cloche.
 *
 * On le repère par la FORME de son `result` (compteurs `assetsFailed`/`assetsTagged`) :
 * au moins un échec ET aucun tag posé. Lecture DÉFENSIVE du `result` (jsonb libre) ;
 * toute autre forme (scan, dédup, move…) ou `result` absent → `false` — on n'énumère
 * PAS les compteurs de succès des autres types (aucun risque de faux positif). PUR.
 */
export function isDoneButAllFailed(job: Job): boolean {
  if (job.status !== 'done') return false;
  const result = job.result;
  if (result === null || result === undefined) return false;
  const failed = numberField(result, 'assetsFailed');
  const tagged = numberField(result, 'assetsTagged');
  if (failed === null || tagged === null) return false;
  return failed > 0 && tagged === 0;
}

/**
 * Un job de tagging IA (`ai_tag`) TERMINÉ a-t-il produit des propositions à RÉVISER ?
 * Vrai si `done` ET `assetsTagged > 0` (≥ 1 photo a reçu de nouvelles propositions).
 * Sert au « pont » de la cloche → action « Réviser les tags » (ouvre la galerie filtrée
 * « Non taguées »). Lecture DÉFENSIVE du `result` (jsonb libre) ; autre type / `result`
 * absent / aucune proposition → `false`. PUR.
 */
export function aiTagHasProposals(job: Job): boolean {
  if (job.type !== 'ai_tag' || job.status !== 'done') return false;
  const result = job.result;
  if (result === null || result === undefined) return false;
  const tagged = numberField(result, 'assetsTagged');
  return tagged !== null && tagged > 0;
}

/**
 * Un job `scan` TERMINÉ a-t-il réellement fait apparaître ou disparaître du contenu ?
 * Vrai si `done` ET son `result` (forme `scanCountsSchema` de `@xphoto/shared`) contient
 * `created > 0` (fichiers nouvellement indexés) OU `missing > 0` (fichiers disparus).
 * Sert au « pont » de la cloche → action « Actualiser la galerie » (recharge in-place)
 * lorsqu'un scan de fond a modifié la bibliothèque hors de la galerie. On se limite
 * VOLONTAIREMENT à `created`/`missing` : un scan qui n'a rien changé (déplacements,
 * réconciliation seule…) ne propose pas d'actualisation. Lecture DÉFENSIVE du `result`
 * (jsonb libre) ; autre type / `result` absent / aucun changement → `false`. PUR, ne lève jamais.
 */
export function scanFoundChanges(job: Job): boolean {
  if (job.type !== 'scan' || job.status !== 'done') return false;
  const result = job.result;
  if (result === null || result === undefined) return false;
  const created = numberField(result, 'created');
  const missing = numberField(result, 'missing');
  return (created !== null && created > 0) || (missing !== null && missing > 0);
}

/**
 * Ensemble des `id` masqués, chacun horodaté à son instant de masquage (ms epoch).
 * L'horodatage borne la croissance DANS LE TEMPS (purge à 24 h) sans dépendre de
 * la présence du job dans le SEED — garantit la durabilité du dismiss au F5.
 */
export type DismissedMap = ReadonlyMap<string, number>;

/**
 * Élague les `id` masqués par l'ÂGE : retire ceux masqués depuis plus de 24 h
 * (`RECENT_WINDOW_MS`), instant `now` injecté (déterministe en test). Même fenêtre
 * que la rétention d'un terminé → au-delà, le job aurait de toute façon disparu de
 * la cloche : dropper le dismiss ne peut PAS le faire réapparaître. On ne s'appuie
 * JAMAIS sur « le job est-il encore dans `jobs` ? » (ce qui vidait tout au boot).
 */
export function pruneDismissed(dismissed: DismissedMap, now: number): Map<string, number> {
  const next = new Map<string, number>();
  for (const [id, at] of dismissed) {
    if (now - at <= RECENT_WINDOW_MS) next.set(id, at);
  }
  return next;
}

/** Vue « ensemble d'ids » d'une `DismissedMap` (pour `computeActivity`). */
export function dismissedIds(dismissed: DismissedMap): Set<string> {
  return new Set(dismissed.keys());
}

// --- Persistance des `id` masqués (localStorage tolérant) ------------------

/**
 * Lit les `id` masqués (avec horodatage) depuis `localStorage` (jamais d'exception).
 *
 * Deux formats acceptés :
 *  - NOUVEAU (v0.25.0) : objet `{ [id]: dismissedAt }` (ms epoch).
 *  - LEGACY (≤ v0.24) : tableau d'`id`. Migré en leur affectant `now` comme instant
 *    de masquage → ils survivent 24 h de plus puis se purgent normalement.
 *
 * `now` est injecté (par défaut `Date.now()`) pour rester testable.
 */
export function readDismissed(now: number = Date.now()): Map<string, number> {
  try {
    const raw = localStorage.getItem(DISMISSED_STORAGE_KEY);
    if (raw === null) return new Map();
    const parsed: unknown = JSON.parse(raw);
    const next = new Map<string, number>();
    if (Array.isArray(parsed)) {
      // Format legacy : tableau d'ids sans horodatage → on les date à `now`.
      for (const v of parsed) if (typeof v === 'string') next.set(v, now);
      return next;
    }
    if (parsed !== null && typeof parsed === 'object') {
      for (const [id, at] of Object.entries(parsed as Record<string, unknown>)) {
        if (typeof at === 'number' && Number.isFinite(at)) next.set(id, at);
      }
    }
    return next;
  } catch {
    return new Map();
  }
}

/** Persiste les `id` masqués horodatés (jamais d'exception si storage indisponible). */
export function writeDismissed(dismissed: DismissedMap): void {
  try {
    localStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify(Object.fromEntries(dismissed)));
  } catch {
    // Storage indisponible (mode privé/quota/test) : l'état mémoire suffit.
  }
}
